import { RefObject } from "react";

import {
  EditableValueCell,
  EditTextArea,
  RowActionButton,
  ValueCell,
} from "../SimpleComponentVisualization";
import {
  ItemContent,
  ItemMomentCell,
  ItemSourceCell,
  ItemView,
} from "./LimitedListCells";
import { itemEditText, ListItem } from "./limitedListItems";
import SortableItemRow, { DragHandleCell } from "./SortableItemRow";

// The open editor, handed to the one row that is editing.
export type RowEditor = {
  text: string;
  isInvalid?: boolean;
  inputRef: RefObject<HTMLTextAreaElement>;
  onChange: (text: string) => void;
  onApply: () => void;
};

// One list row: source, content, and moment cells, with the edit
// affordances in edit mode, sortable when editable.
const LimitedListRow = ({
  item,
  index,
  view,
  editor,
  onStartEdit,
  onDelete,
}: {
  item: ListItem;
  // The item's position in the unsorted data: the sortable id, stable
  // across filtering and the ordering toggle.
  index: number;
  view: ItemView;
  editor: RowEditor | null;
  onStartEdit: (text: string) => void;
  onDelete: () => void;
}) => {
  const editableText = itemEditText(item);
  const content = <ItemContent item={item} view={view} />;
  const sourceCell = <ItemSourceCell item={item} />;
  const momentCell = <ItemMomentCell item={item} view={view} />;

  const valueCell = editor ? (
    <ValueCell $compactView={view.compact}>
      <EditTextArea
        autoFocus
        ref={editor.inputRef}
        value={editor.text}
        isInvalid={editor.isInvalid}
        onChange={(event) => editor.onChange(event.target.value)}
        // Tabbing out of the editor applies like clicking out.
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            editor.onApply();
          }
        }}
        data-testid="limited-list-edit-input"
      />
    </ValueCell>
  ) : view.isEditMode && editableText !== null ? (
    <EditableValueCell
      $compactView={view.compact}
      onClick={() => onStartEdit(editableText)}
      data-testid="limited-list-editable-cell"
    >
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1">{content}</div>
        <RowActionButton
          className="bi-pencil-fill"
          aria-label="Edit item"
          data-testid="limited-list-edit-item"
        />
        <RowActionButton
          className="bi-trash-fill"
          aria-label="Delete item"
          data-testid="limited-list-delete-item"
          onClick={(event) => {
            // Deleting, not editing: the cell's click must not open the
            // editor.
            event.stopPropagation();
            onDelete();
          }}
        />
      </div>
    </EditableValueCell>
  ) : (
    <ValueCell $compactView={view.compact}>{content}</ValueCell>
  );

  if (!view.isEditMode) {
    return (
      <tr data-testid="limited-list-item">
        {sourceCell}
        {valueCell}
        {momentCell}
      </tr>
    );
  }

  return (
    <SortableItemRow id={index}>
      {(dragHandle) => (
        <>
          <DragHandleCell>{dragHandle}</DragHandleCell>
          {sourceCell}
          {valueCell}
          {momentCell}
        </>
      )}
    </SortableItemRow>
  );
};

export default LimitedListRow;
